import { BackHand } from "@mui/icons-material";
import { BorderCrossing, OvernightStay, PitStopStay, RouteMetadata, WeatherDelay } from "../../generated/gpb/api/route";
import { getOvernightType, getWaypointIcon, MarkerType } from "./GeoMarkerUtils";
import { iconStyle } from "./GeoViewStyle";
import rv from "../../static/rv.svg";
import { Box } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDownUpAcrossLine } from "@fortawesome/sharp-duotone-light-svg-icons";

function isInBounds( startTime: number | undefined, endTime: number | undefined, selectedTime: number )
{

    return startTime !== undefined && endTime !== undefined && startTime < selectedTime && endTime > selectedTime;
}
/**
 * Determine and return the appropriate RV symbol based on the current selected time.
 * @param {Array<StayElement>} overnightStays - Array of overnight stays.
 * @param {Array<PitStopStay>} pitStops - Array of weather delays.
 * @param {Array<DelayElement>} weatherDelays - Array of weather delays.
 * @param {number} selectedTime - The current selected time in milliseconds.
 * @param {number} routeRVIconScale - The scale of the RV icon.
 * @param {RouteMetadata} routeMetadata - Metadata about the route.
 * @returns {JSX.Element} JSX Element representing the RV symbol.
 */
export const getRVSymbol = (
    overnightStays: Array<OvernightStay>,
    pitStops: Array<PitStopStay>,
    weatherDelays: Array<WeatherDelay>,
    borderCrossings: Array<BorderCrossing>,
    selectedTime: number,
    routeRVIconScale: number,
    routeMetadata?: RouteMetadata
): JSX.Element =>
{
    const scale = routeRVIconScale * 0.7;

    for ( const stay of overnightStays )
    {
        const startTime = stay.startTime?.getTime();
        const endTime = stay.endTime?.getTime();
        if ( isInBounds( startTime, endTime, selectedTime ) )
        {
            return getWaypointIcon( {
                type: getOvernightType( stay.isPlanned ),
                scale,
                isRVIcon: true
            } );
        }
    }

    for ( const stop of pitStops )
    {
        const startTime = stop.startTime?.getTime();
        const endTime = stop.endTime?.getTime();
        if ( isInBounds( startTime, endTime, selectedTime ) )
        {
            return getWaypointIcon( {
                type: MarkerType.PIT_STOP,
                scale,
                isRVIcon: true
            } );
        }
    }

    for ( const delay of weatherDelays )
    {
        const startTime = delay.startTime?.getTime();
        const endTime = delay.endTime?.getTime();
        if ( isInBounds( startTime, endTime, selectedTime ) )
        {
            return (
                <BackHand
                    style = {iconStyle}
                    sx = {{ color: "red !important" }}
                />
            );
        }
    }

    for ( const crossing of borderCrossings )
    {
        const startTime = crossing.startTime?.getTime();
        const endTime = crossing.endTime?.getTime();
        if ( isInBounds( startTime, endTime, selectedTime ) )
        {
            return (
                <FontAwesomeIcon
                    icon = {faArrowDownUpAcrossLine}
                    size = "lg"
                    fixedWidth
                    style = {{
                        color: "red",
                        paddingTop: "4px",
                        paddingLeft: "3px",
                        scale: `${scale}%`,
                        position: "absolute"
                    }}
                />
            );
        }
    }

    // After route ends
    if ( routeMetadata?.endTime !== undefined && selectedTime > routeMetadata.endTime.getTime() )
    {
        return getWaypointIcon( {
            type: MarkerType.END,
            scale,
            isRVIcon: true
        } );
    }

    // Default RV icon
    return getDefaultRvIcon( 32, "-4px" );
};
export function getDefaultRvIcon( size: number, margin: string | undefined = undefined )
{
    // Default RV icon
    return (
        <Box
            component = "img"
            alt = "rv icon"
            src = {rv}
            sx = {{
                width: size,
                height: size,
                marginLeft: margin,
                marginTop: margin,
                position: "fixed",
                zIndex: 1000,
                color: "red",
                "& path": {
                    fill: "red"
                }
            }}
        />
    );
}

